import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getMyDetailsThunk,
  getMyAvailabilityThunk,
} from "../../store/thunks/adminThunks";
import { clearSuccessMessage } from "../../store/slices/adminSlice";
import { notifyError, notifySuccess } from "../../utils/notification";
import Button from "../../components/common/Button";
import Tabs from "../../components/common/Tabs";
import Badge from "../../components/common/Badge";
import {
  ClockIcon,
  CalendarIcon,
  CogIcon,
  BellIcon,
  ViewGridIcon,
  StatusOnlineIcon,
  UserGroupIcon,
} from "@heroicons/react/outline";

// Import availability components
import AvailabilityDashboard from "./components/Availability/AvailabilityDashboard";
import StatusManager from "./components/Availability/StatusManager";
import WorkingHoursEditor from "./components/Availability/WorkingHoursEditor";
import TimeOffCalendar from "./components/Availability/TimeOffCalendar";
import AppointmentSettings from "./components/Availability/AppointmentSettings";
import SlotViewer from "./components/Availability/SlotViewer";
import NotificationSettings from "./components/Availability/NotificationSettings";
import AdminSearchPanel from "./components/Availability/AdminSearchPanel";

const AvailabilityManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const {
    currentAdmin,
    myAvailability,
    myCurrentStatus,
    appointmentSettings,
    appointmentStats,
    loading,
    loadingAvailability,
    error,
    successMessage,
  } = useSelector((state) => state.admin);

  const { user, isRootAdmin } = useSelector((state) => state.auth);

  // Local state
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Refs to track if data has been loaded and prevent loops
  const hasLoadedRef = useRef(false);
  const errorShownRef = useRef(false);
  const successShownRef = useRef(false);

  // Load initial data ONLY ONCE
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadInitialData();
    }
  }, []); // Empty dependency array - runs only once

  const loadInitialData = async () => {
    try {
      setIsInitialLoading(true);
      await Promise.all([dispatch(getMyAvailabilityThunk()).unwrap()]);
    } catch (error) {
      console.error("Failed to load initial data:", error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  // Handle errors with ref to prevent loops
  useEffect(() => {
    if (error && error !== errorShownRef.current) {
      errorShownRef.current = error;
      notifyError(error);
    }
  }, [error]);

  // Handle success messages with ref to prevent loops
  useEffect(() => {
    if (successMessage && successMessage !== successShownRef.current) {
      successShownRef.current = successMessage;
      notifySuccess(successMessage);

      // Clear after showing
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
        successShownRef.current = null;
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      AVAILABLE: "success",
      BUSY: "warning",
      IN_APPOINTMENT: "info",
      ON_BREAK: "warning",
      OFF_DUTY: "default",
      AWAY: "danger",
    };
    return colors[status] || "default";
  };

  // Calculate statistics
  const stats = {
    upcomingAppointments: appointmentStats?.upcomingAppointmentsCount || 0,
    completedToday: 0,
    rating: appointmentStats?.averageRating || 0,
    totalCompleted: appointmentStats?.completedAppointments || 0,
  };

  // Tab configuration
  const tabs = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: <ViewGridIcon className="w-4 h-4" />,
      badge: stats.upcomingAppointments > 0 ? stats.upcomingAppointments : null,
    },
    {
      id: "status",
      label: "Status",
      icon: <StatusOnlineIcon className="w-4 h-4" />,
      badge: myCurrentStatus?.status ? (
        <Badge
          variant={getStatusColor(myCurrentStatus.status)}
          className="ml-2"
        >
          {myCurrentStatus.status}
        </Badge>
      ) : null,
    },
    {
      id: "working-hours",
      label: "Working Hours",
      icon: <ClockIcon className="w-4 h-4" />,
    },
    {
      id: "time-off",
      label: "Time Off",
      icon: <CalendarIcon className="w-4 h-4" />,
    },
    {
      id: "appointments",
      label: "Appointment Settings",
      icon: <CogIcon className="w-4 h-4" />,
    },
    {
      id: "slots",
      label: "Available Slots",
      icon: <CalendarIcon className="w-4 h-4" />,
    },
    {
      id: "notifications",
      label: "Notifications",
      icon: <BellIcon className="w-4 h-4" />,
    },
    ...(isRootAdmin
      ? [
          {
            id: "search",
            label: "Find Admins",
            icon: <UserGroupIcon className="w-4 h-4" />,
          },
        ]
      : []),
  ];

  // Render tab content
  const renderTabContent = () => {
    if (isInitialLoading) {
      return (
        <div className="flex justify-center items-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading availability data...</p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case "dashboard":
        return (
          <AvailabilityDashboard
            admin={currentAdmin}
            availability={myAvailability}
            status={myCurrentStatus}
            stats={stats}
            onNavigate={setActiveTab}
          />
        );

      case "status":
        return (
          <StatusManager currentStatus={myCurrentStatus} admin={currentAdmin} />
        );

      case "working-hours":
        return (
          <WorkingHoursEditor
            workingHours={myAvailability?.workingHours}
            admin={currentAdmin}
          />
        );

      case "time-off":
        return (
          <TimeOffCalendar
            timeOffList={myAvailability?.timeOff || []}
            specialHoursList={myAvailability?.specialHours || []}
            admin={currentAdmin}
          />
        );

      case "appointments":
        return (
          <AppointmentSettings
            settings={appointmentSettings}
            acceptingAppointments={myAvailability?.acceptsAppointments}
            admin={currentAdmin}
          />
        );

      case "slots":
        return (
          <SlotViewer admin={currentAdmin} availability={myAvailability} />
        );

      case "notifications":
        return <NotificationSettings admin={currentAdmin} />;

      case "search":
        return isRootAdmin ? (
          <AdminSearchPanel />
        ) : (
          <div className="text-center py-12 text-gray-500">Access Denied</div>
        );

      default:
        return (
          <div className="text-center py-12 text-gray-500">
            Select a tab to view content
          </div>
        );
    }
  };

  //   // Check if admin can manage availability
  //   const canManageAvailability = () => {
  //     if (!currentAdmin) return false;
  //     return (
  //       isRootAdmin ||
  //       currentAdmin.role === "admin" ||
  //       currentAdmin.role === "branch_manager"
  //     );
  //   };

  //   // Access check
  //   if (!canManageAvailability()) {
  //     return (
  //       <div className="container mx-auto p-6">
  //         <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
  //           <ClockIcon className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
  //           <h2 className="text-lg font-semibold text-gray-900 mb-2">
  //             Access Restricted
  //           </h2>
  //           <p className="text-gray-600">
  //             You don't have permission to manage availability settings. Please
  //             contact your administrator if you need access.
  //           </p>
  //           <Button
  //             variant="secondary"
  //             className="mt-4"
  //             onClick={() => navigate("/admin")}
  //           >
  //             Back to Admin Dashboard
  //           </Button>
  //         </div>
  //       </div>
  //     );
  //   }

  return (
    <div className="container mx-auto p-4 bg-background text-text">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            {/* <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate("/admin")}
                className="mb-2"
              >
                ← Back
              </Button>
            </div> */}
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ClockIcon className="w-7 h-7 text-primary" />
              Availability Management
            </h1>
            <p className="text-gray-600 mt-1">
              Manage your availability, working hours, and appointment settings
            </p>
          </div>

          {/* Quick Stats */}
          {!isInitialLoading && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white rounded-lg shadow-sm p-3 border">
                <p className="text-xs text-gray-600">Status</p>
                <p className="font-semibold text-sm">
                  <Badge
                    variant={getStatusColor(
                      myCurrentStatus?.status || "OFF_DUTY"
                    )}
                  >
                    {myCurrentStatus?.status || "OFF_DUTY"}
                  </Badge>
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-3 border">
                <p className="text-xs text-gray-600">Upcoming</p>
                <p className="font-semibold text-lg">
                  {stats.upcomingAppointments}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-3 border">
                <p className="text-xs text-gray-600">Rating</p>
                <p className="font-semibold text-lg">
                  {stats.rating > 0 ? `⭐ ${stats.rating}` : "N/A"}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-3 border">
                <p className="text-xs text-gray-600">Completed</p>
                <p className="font-semibold text-lg">{stats.totalCompleted}</p>
              </div>
            </div>
          )}
        </div>

        {/* Accepting Appointments Toggle */}
        {!isInitialLoading && myAvailability && (
          <div className="mt-4 bg-white rounded-lg shadow-sm p-4 border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-3 rounded-full ${
                    myAvailability.acceptsAppointments
                      ? "bg-green-500 animate-pulse"
                      : "bg-gray-400"
                  }`}
                ></div>
                <div>
                  <p className="font-medium text-gray-900">
                    {myAvailability.acceptsAppointments
                      ? "Accepting Appointments"
                      : "Not Accepting Appointments"}
                  </p>
                  <p className="text-sm text-gray-600">
                    {myAvailability.acceptsAppointments
                      ? "Customers can book appointments with you"
                      : "Appointment booking is currently disabled"}
                  </p>
                </div>
              </div>
              <Button
                variant={
                  myAvailability.acceptsAppointments ? "danger" : "success"
                }
                size="sm"
                onClick={() => setActiveTab("appointments")}
              >
                {myAvailability.acceptsAppointments ? "Disable" : "Enable"}{" "}
                Appointments
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        className="mb-6"
      />

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AvailabilityManagement;
